import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { Box } from '@mui/material';

import { usePhoneScreen } from '@/designs-dev/device/PhoneScreenContext';
import { useHideTabBar } from '@/designs-dev/device/TabBarVisibility';

// How long the page takes to arrive, and to leave. Exported because a caller
// that hands one layer over to another has to know when the incoming one has
// covered the screen.
export const SETTLE_MS = 280;

// A full-screen modal presentation covering the whole phone screen: the content
// is a full page rather than a sheet panel. Children get `close()`, which
// animates out and then calls `onClose`.
//
// Which edge it comes from is a question about the bar at the top of it. A page
// with a back chevron is somewhere you have gone from where you were, so it
// comes in from the right and leaves the same way, like PushScreen — the
// chevron points at the direction it will go. A page with an X is a thing put
// in front of you rather than somewhere you went, and those rise from the
// bottom. Either way it leaves the way it arrived, so the gesture reverses.
const ModalScreen = ({
  from = 'bottom',
  onClose,
  children,
}: {
  /** 'right' for a page whose bar has a back chevron. See above. */
  from?: 'bottom' | 'right';
  onClose: () => void;
  children: (close: () => void) => ReactNode;
}) => {
  const [open, setOpen] = useState(false);
  // On the frame after mount, not in the effect itself. A passive effect can be
  // flushed before the browser has painted the closed state, and then the page
  // is already at rest when the transition is set up and it appears
  // without sliding. Which of the two happens depends on what else React was
  // doing, so the same tap slides sometimes and pops others. Same double
  // requestAnimationFrame as BottomSheet, for the same reason.
  useEffect(() => {
    const r = requestAnimationFrame(() =>
      requestAnimationFrame(() => setOpen(true)),
    );
    return () => cancelAnimationFrame(r);
  }, []);
  // The tab bar shouldn't show behind a modal.
  useHideTabBar();

  const closing = useRef(false);
  const close = () => {
    if (closing.current) return;
    closing.current = true;
    setOpen(false);
    window.setTimeout(onClose, SETTLE_MS + 40);
  };

  const screenEl = usePhoneScreen();

  const page = (
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        bgcolor: 'background.default',
        zIndex: 22,
        transform: open
          ? 'translate(0, 0)'
          : from === 'right'
            ? 'translateX(100%)'
            : 'translateY(100%)',
        transition: `transform ${SETTLE_MS}ms ease`,
      }}
    >
      {children(close)}
    </Box>
  );

  return screenEl ? createPortal(page, screenEl) : page;
};

export default ModalScreen;
